import { defineStore } from "pinia";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CWebsocket } from "@/assets/js/websocketHelper";
import { useProfileStore } from "./profile";
import type { TWsData } from "@/assets/js/globalTypes";
dayjs.extend(utc);
dayjs.extend(timezone);

export const useWebSocketStore = defineStore("web-socket", () => {
	const wsText = ref("");
	const wsInstance = ref<CWebsocket | null>(null);

	const profileStore = useProfileStore();
	const wsRoot = import.meta.env.VITE_WEBSOCKET_PATH;

	const wsURL = computed(() => {
		const thisLocation = globalThis.location.host;
		const protocol = globalThis.location.protocol.startsWith("https")
			? "wss"
			: "ws";
		if (thisLocation.startsWith("localhost")) {
			return import.meta.env.VITE_WEBSOCKET_URL;
		}
		return `${protocol}://${thisLocation}${wsRoot}`;
	});

	if (!wsRoot) {
		throw new Error("WEBSOCKET PATH NOT DEFINED");
	}

	// Initialize WebSocket
	function initWs(jwt: string) {
		const wsUrl = wsURL.value + `?jwt=${jwt}`;

		const options: ConstructorParameters<typeof CWebsocket>[1] = {
			cb: {
				onOpen(ws, evt) {
					console.log("WebSocket Opened");
				},
				onMessageData: (ws, evt, data) => {
					wsText.value = ws.logs.join("\n");

					// Process incoming data
					if ((data as unknown as TWsData).type === "new-notification") {
						profileStore.hasNewUnreadNotification = true;
						profileStore.notifications.unshift(
							(data as unknown as TWsData).notification,
						);
					}
				},
				onError(ws, evt) {
					console.error("WebSocket Error", evt);
					wsText.value = ws.logs.join("\n");
				},
				onClose(ws, evt) {
					console.log("WebSocket Closed", evt);
					wsText.value = ws.logs.join("\n");
				},
			},
			useLogs: true,
			autoReconnect: true, // Enable auto-reconnect
			retries: 5, // Maximum retries for reconnection
			delayReconnect: 2000, // Delay between retries
			pingInterval: 30_000,
		};
		wsInstance.value = new CWebsocket(wsUrl, options);
		wsText.value = wsInstance.value.logs.join("\n");
	}

	return {
		initWs,
		wsInstance,
	};
});
